import { useQuery } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { useEffect } from "react";
import { useParams } from "react-router-dom";

import { Loader } from "shared/components/Loader";
import { Receipt } from "./components/Receipt";
import { HugeiconsIcon } from "@hugeicons/react";
import { AlertCircleIcon } from "@hugeicons-pro/core-solid-rounded";
import { getErrorMessage } from "utils/error";

export const ApplicationsIdReportsPrintReceipt = () => {
  const { applicationId } = useParams();
  const urlParams = new URLSearchParams(window.location.search);
  const applicantId = urlParams.get("applicantId");

  // Get the receipt data from the billing activities endpoint
  const { data, isLoading, error } = useQuery({
    queryKey: ["receipt", applicantId],
    queryFn: async () => {
      const response = await instance.get(
        `/portal/v1/applications/${applicationId}/applicants/${applicantId}/receipt`
      );
      return response.data;
    },
    enabled: !!applicantId
  });

  // Get the main billing activity for the application fee (paid status)
  const primaryActivity = data?.find(
    (activity) => activity.category === "applicant_application_fee"
  );

  // Auto-trigger print when data is loaded
  useEffect(() => {
    if (primaryActivity) {
      // Small delay to ensure DOM is rendered before opening print window
      const printTimer = setTimeout(() => {
        const handleAfterPrint = () => {
          window.removeEventListener("afterprint", handleAfterPrint);
          window.close();
        };

        window.addEventListener("afterprint", handleAfterPrint);
        window.print();
      }, 100);

      return () => clearTimeout(printTimer);
    }
  }, [primaryActivity]);

  if (isLoading)
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="flex flex-col items-center justify-center">
          <Loader />
          <h1 className="text-lg">Loading receipt data...</h1>
        </div>
      </div>
    );

  // Error for unpaid applicant or API error
  if (error || !primaryActivity)
    return (
      <div className="flex flex-col h-screen  items-center justify-center">
        <HugeiconsIcon
          icon={AlertCircleIcon}
          size={64}
          className="text-danger-500 mb-6"
        />

        <h1 className="text-lg font-bold mb-2">
          {!primaryActivity
            ? "Receipt can only be printed for paid applicants."
            : "There was an error printing your receipt."}
        </h1>
        <p className="text-base text-content-400">{getErrorMessage(error)}</p>
      </div>
    );

  return (
    <div className="pt-0 pl-4 pr-6 print:block">
      <Receipt billingActivities={data} primaryActivity={primaryActivity} />
    </div>
  );
};
